import { IHeroHeaderCollection, IRichTextItem } from '@/app/types/cms';
import { useAnalytics } from '@/app/utils/useAnalytics';
import Button from '@/components/Button';
import { TickList } from '@/components/TickList';
import clsx from 'clsx';
import Image from 'next/image';
import ReactMarkdown from 'react-markdown';

import styles from './HeroHeaderComponent.module.scss';

interface HeroHeaderComponentProps {
  hero_header_collection: IHeroHeaderCollection;
  imageAlignment?: 'left' | 'right';
  darkMode?: boolean;
}

const longHeadingThreshold = 50;

const HeroHeaderComponent = ({
  hero_header_collection,
  imageAlignment = 'left',
  darkMode,
}: HeroHeaderComponentProps) => {
  const track = useAnalytics();
  const {
    heading,
    heroParagraph,
    bulletList,
    primaryButtonText,
    primaryButtonUrl,
    secondaryButtonText,
    secondaryButtonUrl,
    proofText,
    heroImage,
    collageImage,
  } = hero_header_collection;
  const isLongHeading = heading.length > longHeadingThreshold;

  let heroImageUrl = '';

  try {
    heroImageUrl = heroImage.url ?? '';
  } catch {
    // nothing to be done here
  }

  return (
    <div className={clsx(styles.heroHeaderContainer, darkMode && styles.darkMode)}>
      <div
        className={clsx(styles.contentContainer, imageAlignment === 'right' && styles.imageRight)}
      >
        <div className={styles.contentTextContainer}>
          <ReactMarkdown className={clsx(styles.heading, isLongHeading && styles.longHeading)}>
            {heading}
          </ReactMarkdown>
          {heroParagraph && <p className={styles.paragraph}>{heroParagraph}</p>}
          {bulletList && (
            <TickList<IRichTextItem>
              className={styles.heroTickList}
              variant="medrecruitTick"
              items={bulletList}
              itemRenderFunction={item => <ReactMarkdown>{item.richTextItem ?? ''}</ReactMarkdown>}
            />
          )}
          <div className={styles.buttonContainer}>
            <Button
              colorVariant={darkMode ? 'secondary' : 'primary'}
              href={primaryButtonUrl}
              trackFunction={eventProps => {
                track('Click', {
                  ...eventProps,
                  action: 'Hero header primary cta',
                });
              }}
            >
              {primaryButtonText}
            </Button>
            {secondaryButtonText && secondaryButtonUrl && (
              <Button
                colorVariant={darkMode ? 'secondary' : 'primary'}
                href={secondaryButtonUrl}
                variant="outlined"
                trackFunction={eventProps => {
                  track('Click', {
                    ...eventProps,
                    action: 'Hero header secondary cta',
                  });
                }}
              >
                {secondaryButtonText}
              </Button>
            )}
          </div>
          <div className={styles.miniTestimonialContainer}>
            {collageImage && (
              <div className={styles.collageImageWrapper}>
                <Image
                  alt=""
                  src={collageImage.url ?? ''}
                  aria-hidden="true"
                  fill
                  sizes="100vw"
                  priority
                />
              </div>
            )}
            {proofText && (
              <ReactMarkdown className={styles.testimonialText}>{proofText}</ReactMarkdown>
            )}
          </div>
        </div>
        <div className={styles.imageContainer}>
          <Image
            alt=""
            src={heroImageUrl}
            aria-hidden="true"
            width={588}
            height={440}
            priority
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default HeroHeaderComponent;
